import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import Layout, { Container } from "../components/layout";
import { getCookie } from "../config/webStorage";

const Badge = ({ status }) => {
  const colors = {
    created: "bg-gray-100 text-gray-700",
    hired: "bg-green-100 text-green-700",
    handoverToTrainer: "bg-blue-100 text-blue-700",
    dropped: "bg-red-100 text-red-700",
    joined: "bg-purple-100 text-purple-700",
  };

  return (
    <span
      className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
};

const BatchDetails = () => {
  const { batchObjId } = useParams();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatchDetails();
    // eslint-disable-next-line
  }, []);

  const fetchBatchDetails = async () => {
    try {
      const token = getCookie("swtoken");
      const { data } = await axios.get(
        `${apiurl}/api/batch/get/${batchObjId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      setBatch(data.batch);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch batch");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg font-medium text-gray-600 animate-pulse">
          Loading batch details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600 font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="min-h-screen bg-gray-50 p-6 space-y-6">
          {/* Batch Header */}
          <div className="bg-white shadow-sm rounded-2xl p-6 border">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-800">
                {batch.batchName} ({batch.id})
              </h1>
              <Badge status={batch.batchStatus} />
            </div>
            <p className="text-gray-600 mt-2">{batch.description}</p>
            <div className="flex justify-between mt-5">
              <div className="flex flex-wrap gap-6 text-sm items-center">
                <div>
                  <span className="text-gray-500">Batch Date:</span>
                  <p className="font-medium">
                    {new Date(batch.batchDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">
                    {new Date(batch.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="">
                  <h2 className=" font-semibold text-gray-800">Created By</h2>
                  <p className="text-gray-700">
                    {batch.createdBy?.userName} ({batch.createdBy?.userEmail})
                  </p>
                </div>
              </div>
              <div className="">
                <p className="text-6xl">
                  {batch?.reports?.totalApplications || "0"}
                </p>
              </div>
            </div>
          </div>

          {/* Reports */}
          <div className="bg-white shadow-md rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Reports
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(batch?.reports?.statusBreakdown).map(
                ([key, value]) => (
                  <div
                    key={key}
                    className="bg-gray-50 border rounded-xl p-4 shadow hover:shadow-md transition"
                  >
                    <p className="text-sm text-gray-500 capitalize">{key}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {value.count}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Applications */}
          <div className="bg-white shadow-md rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-3 text-gray-800">
              Applications
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3">Application ID</th>
                    <th className="p-3">Candidate</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Phone</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Induction</th>
                    <th className="p-3">Training</th>
                    <th className="p-3">OJT</th>
                    <th className="p-3">Floor</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.applications.map((app) => (
                    <tr
                      key={app._id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="p-3">{app.applicationId}</td>
                      <td className="p-3 font-medium">
                        {app.applicationDetails?.candidateSnapshot?.firstName}{" "}
                        {app.applicationDetails?.candidateSnapshot?.lastName}
                      </td>
                      <td className="p-3">
                        {app.applicationDetails?.candidateSnapshot?.email}
                      </td>
                      <td className="p-3">
                        {app.applicationDetails?.candidateSnapshot?.phone}
                      </td>
                      <td className="p-3">
                        <Badge status={app.applicationStatus} />
                      </td>
                      <td className="p-3">{app.inductionStatus}</td>
                      <td className="p-3">{app.trainingStatus}</td>
                      <td className="p-3">{app.ojtStatus}</td>
                      <td className="p-3">{app.floorStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* History (Timeline style) */}
          <div className="bg-white shadow-md rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-3 text-gray-800">
              History
            </h2>
            <div className="relative border-l-2 border-gray-200 pl-4 space-y-4">
              {batch.history.map((h, idx) => (
                <div key={idx} className="relative">
                  {/* <div className="absolute -left-3 top-1 w-6 h-6 bg-blue-500 rounded-full border-2 border-white"></div> */}
                  <div className="bg-gray-50 p-3 rounded-lg shadow-sm">
                    <p className="text-gray-700 font-medium">{h.status}</p>
                    <p className="text-sm text-gray-500">{h.notes}</p>
                    <p className="text-sm text-gray-500">{h.remarks}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      By {h.changedBy?.userName} on{" "}
                      {new Date(h.changedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default BatchDetails;
